import { useMemo } from 'react';

/**
 * Minimal, dependency-free Markdown renderer.
 *
 * Renders the subset an agent answer actually uses — headings, bold/italic,
 * inline + fenced code, ordered/unordered lists, blockquotes, links, rules and
 * paragraphs — as real React nodes. No dangerouslySetInnerHTML, so untrusted
 * model output can never inject markup; links are constrained to http(s).
 */

function renderInline(text, keyBase) {
  // Tokenize inline spans: `code`, **bold**, *italic*, [label](url).
  const nodes = [];
  let i = 0;
  let buf = '';
  let k = 0;
  const flush = () => {
    if (buf) {
      nodes.push(buf);
      buf = '';
    }
  };
  const pushEl = (el) => {
    flush();
    nodes.push(el);
  };

  while (i < text.length) {
    const rest = text.slice(i);

    // inline code
    let m = /^`([^`]+)`/.exec(rest);
    if (m) {
      pushEl(<code key={`${keyBase}-c${k++}`} className="md-code">{m[1]}</code>);
      i += m[0].length;
      continue;
    }
    // link [label](url)
    m = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/.exec(rest);
    if (m) {
      pushEl(
        <a key={`${keyBase}-l${k++}`} href={m[2]} target="_blank" rel="noreferrer" className="md-link">
          {m[1]}
        </a>,
      );
      i += m[0].length;
      continue;
    }
    // bare url
    m = /^(https?:\/\/[^\s)]+)/.exec(rest);
    if (m) {
      pushEl(
        <a key={`${keyBase}-u${k++}`} href={m[1]} target="_blank" rel="noreferrer" className="md-link">
          {m[1]}
        </a>,
      );
      i += m[0].length;
      continue;
    }
    // bold **x** or __x__
    m = /^(\*\*|__)(.+?)\1/.exec(rest);
    if (m) {
      pushEl(<strong key={`${keyBase}-b${k++}`}>{renderInline(m[2], `${keyBase}-b${k}`)}</strong>);
      i += m[0].length;
      continue;
    }
    // italic *x* or _x_
    m = /^(\*|_)(?!\s)(.+?)(?<!\s)\1/.exec(rest);
    if (m) {
      pushEl(<em key={`${keyBase}-i${k++}`}>{renderInline(m[2], `${keyBase}-i${k}`)}</em>);
      i += m[0].length;
      continue;
    }
    buf += text[i];
    i += 1;
  }
  flush();
  return nodes;
}

function parseBlocks(src) {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    let line = lines[i];

    // fenced code
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const code = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence
      blocks.push({ type: 'code', lang: fence[1] || '', text: code.join('\n') });
      continue;
    }

    // blank
    if (/^\s*$/.test(line)) {
      i += 1;
      continue;
    }

    // heading
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ type: 'heading', level: h[1].length, text: h[2].trim() });
      i += 1;
      continue;
    }

    // horizontal rule
    if (/^\s*([-*_])\1\1+\s*$/.test(line)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    // blockquote
    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', text: quote.join('\n') });
      continue;
    }

    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // ordered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // paragraph (gather until blank or block starter)
    const para = [];
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !/^```/.test(lines[i]) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'p', text: para.join('\n') });
  }
  return blocks;
}

export function Markdown({ text, className }) {
  const blocks = useMemo(() => parseBlocks(String(text ?? '')), [text]);

  return (
    <div className={`md ${className ?? ''}`}>
      {blocks.map((b, idx) => {
        const key = `blk-${idx}`;
        switch (b.type) {
          case 'heading': {
            const Tag = `h${Math.min(b.level + 1, 6)}`;
            return <Tag key={key} className="md-h">{renderInline(b.text, key)}</Tag>;
          }
          case 'code':
            return (
              <pre key={key} className="md-pre">
                <code>{b.text}</code>
              </pre>
            );
          case 'hr':
            return <hr key={key} className="md-hr" />;
          case 'quote':
            return (
              <blockquote key={key} className="md-quote">
                {parseBlocks(b.text).map((qb, qi) => (
                  <p key={`${key}-q${qi}`}>{renderInline(qb.text ?? '', `${key}-q${qi}`)}</p>
                ))}
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={key} className="md-ul">
                {b.items.map((it, ii) => (
                  <li key={`${key}-${ii}`}>{renderInline(it, `${key}-${ii}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="md-ol">
                {b.items.map((it, ii) => (
                  <li key={`${key}-${ii}`}>{renderInline(it, `${key}-${ii}`)}</li>
                ))}
              </ol>
            );
          default:
            return <p key={key} className="md-p">{renderInline(b.text, key)}</p>;
        }
      })}
    </div>
  );
}
